import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Clock } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../utils/api';

function Register() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ email: '', password: '', name: '' });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${API}/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        const data = await response.json();
        localStorage.setItem('access_token', data.access_token);
        localStorage.setItem('user', JSON.stringify(data.user));
        
        if (data.user.role === 'admin') {
          toast.success('Welcome! You are the first user and have been made an admin.');
        } else {
          toast.success('Registration successful!');
        }
        
        navigate('/dashboard');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Registration failed');
      }
    } catch (error) {
      toast.error('Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <div className="bg-white rounded-2xl shadow-lg p-8">
          <div className="flex items-center justify-center mb-8">
            <div className="bg-primary rounded-full p-4">
              <Clock className="w-12 h-12 text-white" />
            </div>
          </div>
          
          <h1 className="text-4xl font-heading font-bold text-center text-foreground-DEFAULT mb-2">
            Join TimeFlow
          </h1>
          <p className="text-center text-foreground-muted mb-8">
            Create your account to get started
          </p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-foreground-DEFAULT mb-2">
                Full Name
              </label>
              <input
                id="name"
                type="text"
                required
                data-testid="register-name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-4 py-3 bg-slate-50 border-transparent focus:bg-white focus:border-primary/20 focus:ring-2 focus:ring-primary/10 transition-all rounded-lg"
                placeholder="John Doe"
              />
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-foreground-DEFAULT mb-2">
                Email
              </label>
              <input
                id="email"
                type="email"
                required
                data-testid="register-email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full px-4 py-3 bg-slate-50 border-transparent focus:bg-white focus:border-primary/20 focus:ring-2 focus:ring-primary/10 transition-all rounded-lg"
                placeholder="you@example.com"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-foreground-DEFAULT mb-2">
                Password
              </label>
              <input
                id="password"
                type="password"
                required
                minLength={6}
                data-testid="register-password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="w-full px-4 py-3 bg-slate-50 border-transparent focus:bg-white focus:border-primary/20 focus:ring-2 focus:ring-primary/10 transition-all rounded-lg"
                placeholder="••••••••"
              />
              <p className="text-xs text-foreground-muted mt-1">Minimum 6 characters</p>
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="register-btn"
              className="w-full bg-primary hover:bg-primary-hover text-white font-medium py-3 px-6 rounded-full shadow-lg shadow-primary/20 transition-all hover:-translate-y-0.5 active:translate-y-0 disabled:opacity-50"
            >
              {loading ? 'Creating account...' : 'Sign Up'}
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-foreground-muted">
              Already have an account?{' '}
              <Link to="/login" className="text-primary hover:text-primary-hover font-medium">
                Log in
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Register;